const LOCATION_TIMEOUT_MS = 10000;

const ZERO_LOCATION = {
  coords: {
    latitude: 0,
    longitude: 0,
    altitude: 0,
    accuracy: 0,
    altitudeAccuracy: 0,
    heading: 0,
    speed: 0,
  },
  timestamp: 0,
};

const parseMessageData = (data) => {
  if (typeof data !== 'string') return data;
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
};

export const getLocationCoords = (location) => {
  const lat = Number(
    location?.coords?.latitude ?? location?.latitude ?? location?.lat
  );
  const lng = Number(
    location?.coords?.longitude ?? location?.longitude ?? location?.lng
  );

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  return { lat, lng };
};

const fromBrowserPosition = (position) => ({
  coords: {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    altitude: position.coords.altitude || 0,
    accuracy: position.coords.accuracy || 0,
    altitudeAccuracy: position.coords.altitudeAccuracy || 0,
    heading: position.coords.heading || 0,
    speed: position.coords.speed || 0,
  },
  timestamp: position.timestamp || Date.now(),
});

const requestNativeLocation = (timeoutMs) =>
  new Promise((resolve) => {
    if (!window.ReactNativeWebView) {
      resolve(null);
      return;
    }

    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('message', handleMessage);
      resolve(value);
    };

    const handleMessage = (event) => {
      const message = parseMessageData(event?.data);
      if (message?.type !== 'location' || !message?.data) return;
      finish(message.data);
    };

    const timer = window.setTimeout(() => finish(null), timeoutMs);
    window.addEventListener('message', handleMessage);
    document.addEventListener('message', handleMessage);
    window.ReactNativeWebView.postMessage(JSON.stringify({ data: 'location' }));
  });

const requestBrowserLocation = (timeoutMs) =>
  new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(fromBrowserPosition(position)),
      () => resolve(null),
      {
        enableHighAccuracy: true,
        timeout: timeoutMs,
        maximumAge: 0,
      }
    );
  });

export default async function getLocation({ timeoutMs = LOCATION_TIMEOUT_MS } = {}) {
  const nativeLocation = await requestNativeLocation(timeoutMs);
  if (getLocationCoords(nativeLocation)) return nativeLocation;

  const browserLocation = await requestBrowserLocation(timeoutMs);
  if (getLocationCoords(browserLocation)) return browserLocation;

  return ZERO_LOCATION;
}
